import React, { useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { assetNavigationHref } from '../../utils/assetOpener';
import { Activity, Pencil, Plus, FileText, ExternalLink } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Blank string → null (preserve null≠0). "0" → 0 (measured zero).
const numOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
};
const round = (n, d = 1) => (n === null || n === undefined ? null
  : Math.round(n * 10 ** d) / 10 ** d);
const fmt = (v) => (v === null || v === undefined || v === '' ? '—' : v);
const isUuid = (s) => typeof s === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);

const emptyGpsForm = () => ({
  activityTimeMin: '', distanceKm: '', topSpeed: '',
  hsrM: '', sprintM: '', sprintCount: '', playerLoad: '',
});

const formFromGps = (gps) => {
  const m = gps?.metrics || {};
  const secs = gps?.active_duration_seconds;
  return {
    activityTimeMin: secs != null ? round(secs / 60, 2) : '',
    distanceKm: m.total_distance_m != null ? round(m.total_distance_m / 1000, 3) : '',
    topSpeed: m.max_speed_kmh ?? '',
    hsrM: m.high_speed_running_m ?? '',
    sprintM: m.sprint_distance_m ?? '',
    sprintCount: m.sprint_count ?? '',
    playerLoad: m.player_load ?? '',
  };
};

const MetricCard = ({ label, value, unit, muted }) => (
  <div className="bg-gray-50 rounded-lg p-2.5" data-testid={`gps-metric-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <p className="text-xs text-gray-400">{label}</p>
    <p className={`font-semibold ${muted ? 'text-gray-500' : ''}`}>
      {fmt(value)}{value !== null && value !== undefined && value !== '' && unit ? ` ${unit}` : ''}
    </p>
  </div>
);

export const MatchPhysicalGPSPanel = ({ perf, matchId, athleteId, activityDate, canEdit, onSaved }) => {
  const gps = perf?.gps && perf.gps.gps_activity_id ? perf.gps : null;
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyGpsForm());
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const openAdd = () => { setForm(emptyGpsForm()); setOpen(true); };
  const openEdit = () => { setForm(formFromGps(gps)); setOpen(true); };

  // Live derived Distance / Min preview inside the form.
  const previewPerMin = useMemo(() => {
    const min = numOrNull(form.activityTimeMin);
    const km = numOrNull(form.distanceKm);
    if (!min || min <= 0 || km === null) return null;
    return round((km * 1000) / min, 1);
  }, [form.activityTimeMin, form.distanceKm]);

  const save = async () => {
    const totalM = numOrNull(form.distanceKm) === null ? null : Math.round(numOrNull(form.distanceKm) * 1000);
    const secs = numOrNull(form.activityTimeMin) === null ? null : Math.round(numOrNull(form.activityTimeMin) * 60);
    const hsr = numOrNull(form.hsrM);
    const sprint = numOrNull(form.sprintM);
    // Client-side guards (backend authoritative).
    if (totalM !== null) {
      if (hsr !== null && hsr > totalM) { toast.error('High-Speed Running cannot exceed Distance Covered'); return; }
      if (sprint !== null && sprint > totalM) { toast.error('Sprint Distance cannot exceed Distance Covered'); return; }
    }
    const metrics = {
      total_distance_m: totalM,
      high_speed_running_m: hsr,
      sprint_distance_m: sprint,
      max_speed_kmh: numOrNull(form.topSpeed),
      player_load: numOrNull(form.playerLoad),
      sprint_count: numOrNull(form.sprintCount) === null ? null : Math.round(numOrNull(form.sprintCount)),
    };

    setSaving(true);
    try {
      // Optional source-report upload first (reuse generic attachment endpoint).
      let sourceReport;
      const file = fileRef.current?.files?.[0];
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        const up = await axios.post(`${API}/attachments/upload`, fd);
        sourceReport = up.data;
      }

      if (gps) {
        const payload = { active_duration_seconds: secs, metrics };
        if (sourceReport) payload.source_report = sourceReport;
        await axios.put(`${API}/gps-activities/${gps.gps_activity_id}`, payload);
      } else {
        const payload = {
          athlete_id: athleteId,
          activity_type: 'match',
          activity_date: activityDate,
          match_v3_id: matchId,
          provider: 'Oliver Sports',
          ingestion_method: 'manual',
          active_duration_seconds: secs,
          metrics,
        };
        if (sourceReport) payload.source_report = sourceReport;
        await axios.post(`${API}/gps-activities`, payload);
      }
      toast.success(gps ? 'Physical / GPS data updated' : 'Physical / GPS data saved');
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save Physical / GPS data');
    } finally { setSaving(false); }
  };

  const d = gps?.derived || {};
  const m = gps?.metrics || {};
  const cards = gps ? [
    { label: 'Activity Time', value: gps.active_duration_seconds != null ? round(gps.active_duration_seconds / 60, 1) : null, unit: 'min' },
    { label: 'Distance Covered', value: m.total_distance_m != null ? round(m.total_distance_m / 1000, 2) : null, unit: 'km' },
    { label: 'Distance / Min', value: d.distance_per_minute ?? null, unit: 'm/min', muted: true },
    { label: 'Top Speed', value: m.max_speed_kmh ?? null, unit: 'km/h' },
    { label: 'High-Speed Running', value: m.high_speed_running_m ?? null, unit: 'm' },
    { label: 'HSR / Min', value: d.high_speed_running_per_minute ?? null, unit: 'm/min', muted: true },
    { label: 'Sprint Distance', value: m.sprint_distance_m ?? null, unit: 'm' },
    { label: 'Sprint Distance / Min', value: d.sprint_distance_per_minute ?? null, unit: 'm/min', muted: true },
    { label: 'Sprint Count', value: m.sprint_count ?? null, unit: '' },
    { label: 'Player Load', value: m.player_load ?? null, unit: 'AU' },
  ].filter((c) => c.value !== null && c.value !== undefined) : [];

  return (
    <Card data-testid="gps-tab">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Activity className="w-4 h-4 text-emerald-600" /> Physical / GPS
        </CardTitle>
        {canEdit && gps && (
          <Button size="sm" variant="outline" onClick={openEdit} data-testid="gps-edit-btn">
            <Pencil className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {gps ? (
          <>
            {cards.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm" data-testid="gps-metrics-grid">
                {cards.map((c) => <MetricCard key={c.label} {...c} />)}
              </div>
            ) : (
              <p className="text-sm text-gray-400">No numeric metrics recorded — see source report.</p>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 pt-1 border-t" data-testid="gps-provenance">
              <span>Source: <span className="font-medium text-gray-700">{gps.provider || 'Oliver Sports'}</span></span>
              <span>Entered manually{gps.entered_by && !isUuid(gps.entered_by) ? ` by ${gps.entered_by}` : ''}</span>
              {gps.entered_at && <span>{new Date(gps.entered_at).toLocaleDateString()}</span>}
            </div>

            {gps.source_report?.url && (
              <a href={assetNavigationHref(gps.source_report.url, gps.source_report.name || 'Oliver Source Report')}
                 target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 text-sm text-sky-600 hover:underline"
                 data-testid="gps-source-report-link">
                <FileText className="w-3.5 h-3.5" /> {gps.source_report.name || 'Oliver Source Report'}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </>
        ) : (
          <div className="py-8 text-center" data-testid="gps-empty">
            <p className="text-sm text-gray-400 mb-3">No Physical / GPS data recorded.</p>
            {canEdit && (
              <Button size="sm" onClick={openAdd} data-testid="gps-add-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Oliver / GPS Data
              </Button>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="gps-form-dialog">
          <DialogHeader>
            <DialogTitle>{gps ? 'Edit' : 'Add'} Physical / GPS Data</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div className="text-xs text-gray-500">
              Source: <span className="font-medium text-gray-700">Oliver Sports</span> · entered manually.
              Leave a field blank if the report does not provide that number — blank is stored as “not recorded”, not zero.
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Activity</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Activity Time (min)</Label>
                  <Input type="number" step="0.1" min="0" value={form.activityTimeMin}
                    onChange={set('activityTimeMin')} data-testid="gps-input-activity-time" />
                </div>
                <div>
                  <Label className="text-xs">Distance Covered (km)</Label>
                  <Input type="number" step="0.01" min="0" value={form.distanceKm}
                    onChange={set('distanceKm')} data-testid="gps-input-distance" />
                </div>
              </div>
              {previewPerMin !== null && (
                <p className="text-xs text-gray-500 mt-1.5" data-testid="gps-derived-preview">
                  Distance / Min (calculated): <span className="font-medium">{previewPerMin} m/min</span>
                </p>
              )}
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Physical Output</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Top Speed (km/h)</Label>
                  <Input type="number" step="0.1" min="0" value={form.topSpeed}
                    onChange={set('topSpeed')} data-testid="gps-input-top-speed" />
                </div>
                <div>
                  <Label className="text-xs">High-Speed Running (m) — optional</Label>
                  <Input type="number" step="1" min="0" value={form.hsrM}
                    onChange={set('hsrM')} data-testid="gps-input-hsr" />
                </div>
                <div>
                  <Label className="text-xs">Sprint Distance (m) — optional</Label>
                  <Input type="number" step="1" min="0" value={form.sprintM}
                    onChange={set('sprintM')} data-testid="gps-input-sprint-distance" />
                </div>
                <div>
                  <Label className="text-xs">Sprint Count — optional</Label>
                  <Input type="number" step="1" min="0" value={form.sprintCount}
                    onChange={set('sprintCount')} data-testid="gps-input-sprint-count" />
                </div>
                <div>
                  <Label className="text-xs">Player Load — optional</Label>
                  <Input type="number" step="0.1" min="0" value={form.playerLoad}
                    onChange={set('playerLoad')} data-testid="gps-input-player-load" />
                </div>
              </div>
            </div>

            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Evidence</p>
              <Label className="text-xs">Source Report (Oliver PDF / image) — optional</Label>
              <Input type="file" ref={fileRef} accept=".pdf,image/*" data-testid="gps-input-source-report" />
              {gps?.source_report?.name && (
                <p className="text-xs text-gray-400 mt-1">Current: {gps.source_report.name} (upload a new file to replace)</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="gps-cancel-btn">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="gps-save-btn">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
